import React, { useMemo } from 'react';
import { AddOnCard, AddOnCardProps } from '../AddOnCard';
import { AddOnItem, AddOnPrice, AddOnType } from '~/types/models/addon';
import { BillingCycle, Service } from '~/types/models';
import {
  AddOnDescriptions,
  AddOnIcons,
  AddOnLabels,
  getFormattedPrice,
} from '../../../../utils';
import {
  RollUpLimitAddOnLearnMoreModal,
  openRollUpLimitAddOnLearnMoreModal,
  closeRollUpLimitAddOnLearnMoreModal,
  RollUpLimitAddOnLearnMoreModalProps,
} from './RollUpLimitAddOnLearnMoreModal';
import {
  RollUpLimitAddOnModal,
  closeRollUpLimitModal,
  openRollUpLimitAddOnModal,
} from './RollUpLimitAddOnModal';
import RollUpLimitAddOnPaymentModal, {
  openRollUpLimitAddOnPaymentModal,
} from './RollUpLimitAddOnPaymentModal';
import useCurrentTeam from '~/react-hooks/use-current-team';
import { CheckoutType } from '~/App/Modules/NewBilling/Pages/Checkout';
import qs from 'query-string';
import {
  RollUpLimitAddOnCancelModal,
  openRollUpLimitAddOnCancelModal,
} from './RollUpLimitAddOnCancelModal';
import { ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import useAnalytics from '~/react-hooks/use-analytics';
import { CHECKOUT_STARTED } from '~/generated/AnalyticsTypes';
import { UpgradeModalViewContext } from '~/App/Services/BillingAnalytics';
import { billingRoutes } from '~/App/Modules/NewBilling';
import { useResumeAddOn } from '~/App/Modules/NewBilling/Hooks/useSubscription';

export interface RollupLimitAddOnCardProps
  extends Pick<
    AddOnCardProps,
    | 'className'
    | 'descriptionClassName'
    | 'billingContainerClassName'
    | 'headerContainerClassName'
    | 'buttonContainerClassName'
  > {
  price?: AddOnPrice;
  details?: AddOnItem;
  billingCycle?: BillingCycle;
}

export function RollupLimitAddOnCard({
  price,
  details,
  billingCycle,
  ...otherProps
}: RollupLimitAddOnCardProps) {
  const { has_valid_card_on_file: hasValidCardOnFile } = useCurrentTeam();
  const analytics = useAnalytics();
  const { onResumeClick } = useResumeAddOn(details?.uuid);
  const monthlyPriceItem = price?.addons.find(
    (item) => item.interval === BillingCycle.Monthly && item.is_active
  );
  const yearlyPriceItem = price?.addons.find(
    (item) => item.interval === BillingCycle.Yearly && item.is_active
  );

  const onCtaClick = () => {
    openRollUpLimitAddOnModal();
  };

  const onLearnMoreClick = () => {
    openRollUpLimitAddOnLearnMoreModal();
  };

  const onLearnMoreCtaClick = () => {
    closeRollUpLimitAddOnLearnMoreModal();
    onCtaClick();
  };

  const onContinueClick = () => {
    closeRollUpLimitModal();
    closeRollUpLimitAddOnLearnMoreModal();

    const context = UpgradeModalViewContext.GrowthLiteAddon;
    analytics.track(CHECKOUT_STARTED, { context });

    if (!hasValidCardOnFile) {
      window.location.href = `${billingRoutes.checkout}${
        CheckoutType.AddOns
      }/?${qs.stringify({
        items: AddOnType.LimitGreetRollUp,
        redirect: window.location.pathname,
        context,
      })}`;
    } else {
      openRollUpLimitAddOnPaymentModal();
    }
  };

  const monthlyPrice = monthlyPriceItem?.amount as unknown as number;
  const yearlyPrice = yearlyPriceItem?.amount as unknown as number;

  const onCancelClick = (uuid: string) =>
    openRollUpLimitAddOnCancelModal({ addOnUuid: uuid });

  const addOnCardProps: AddOnCardProps = useMemo(() => {
    const props: AddOnCardProps = {
      ...otherProps,
      descriptionClassName: 'lg:h-[150px] xl:h-[100px]',
      imgUrl: AddOnIcons[AddOnType.LimitGreetRollUp][Service.VideoMessaging],
      title: AddOnLabels[AddOnType.LimitGreetRollUp],
      description:
        AddOnDescriptions[AddOnType.LimitGreetRollUp][Service.VideoMessaging],
      billingPrice: `$${monthlyPrice}`,
      billingCycle: '/mo',
      billingDescription:
        billingCycle === BillingCycle.Yearly
          ? `Billed $${getFormattedPrice(yearlyPrice)} USD annually`
          : undefined,
      ctaText: 'Get Growth lite add-on',
      onCtaClick,
      onLearnMoreClick,
    };

    if (details?.uuid) {
      return {
        ...props,
        billingPrice: 'Added',
        billingCycle: undefined,
        onCancelClick: () => onCancelClick(details?.uuid),
        onCtaClick: undefined,
        cancelled: details?.cancelled,
        uuid: details?.uuid,
        cancelledText: details?.cancelledText,
      };
    }

    return props;
  }, [billingCycle, details, monthlyPriceItem, yearlyPriceItem]);

  const amount = useMemo(() => {
    const amount =
      billingCycle === BillingCycle.Yearly
        ? yearlyPriceItem?.amount
        : monthlyPriceItem?.amount;

    return amount ?? 0;
  }, [yearlyPriceItem, monthlyPriceItem]);

  const learnMoreModalProps: RollUpLimitAddOnLearnMoreModalProps =
    useMemo(() => {
      const props = {
        monthlyPrice,
        onCtaClick: onLearnMoreCtaClick,
      };

      if (details?.uuid) {
        return {
          ...props,
          onCtaClick: () => {
            closeRollUpLimitAddOnLearnMoreModal();

            if (details?.cancelled) {
              void onResumeClick();
            } else {
              onCancelClick(details?.uuid);
            }
          },
          ctaText: details?.cancelled ? 'Resume add-on' : 'Cancel add-on',
          ctaVariant: ButtonVariant.Secondary,
        };
      }

      return props;
    }, [details, monthlyPrice]);

  if (!monthlyPriceItem || !yearlyPriceItem) return <></>;

  return (
    <>
      <AddOnCard {...addOnCardProps} />
      <RollUpLimitAddOnModal
        onCtaClick={onContinueClick}
        monthlyPrice={monthlyPrice}
      />
      <RollUpLimitAddOnLearnMoreModal {...learnMoreModalProps} />
      <RollUpLimitAddOnCancelModal />
      <RollUpLimitAddOnPaymentModal amount={amount} />
    </>
  );
}
